// @flow
import React, { useContext, useState } from 'react';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { object } from 'yup';
import { parse } from 'papaparse';
import {
  useAddClusterMutation,
  useUpdateClusterMutation,
  useAddFlinkPoolMutation,
  useUpdateFlinkPoolMutation,
  useCreateStreamGovernanceConfigurationMutation,
} from '@src/service-definitions/estimates';
import { StyledContainer } from '@src/common-utils/styledComponents';
import { IfElse } from '@presentational/IfElse';
import { TinyHeaderSpan } from '@presentational/headings/TinyHeaderSpan';
import { BulletedList } from '@presentational/lists/BulletedList';
import { ConfirmModal } from '@presentational/modals/ConfirmModal';
import { toastError } from '@presentational/notifications/utils';
import { useEstimateVersion } from '@src/custom-hooks/useEstimateVersion';
import { useEstimateDealDurationMonths } from '@src/custom-hooks/useEstimateDealDurationMonths';
import { useEstimateRateCardVersion } from '@src/custom-hooks/useEstimateRateCardVersion';
import { useEstimateRateCards } from '@src/custom-hooks/useEstimateRateCards';
import { InlineLoader } from '@presentational/InlineLoader';
import { Spacer } from '@presentational/spacing/Spacer';
import { Form } from '@src/formik-utils/formikSUIWrapper';
import { getValidationSchemaFromColsConfig } from '@src/configuration/utils';
import { trimObjectKeys, getRateCardLabelByName } from '@src/common-utils/utils';
import { EstimateContext } from '@src/contexts/EstimateContext';
import { MonthlyInputsMetaDataContext } from '@src/contexts/MonthlyInputsMetaDataContext';
import { processBaselineImport, formResetHandler } from '@components/ce1-baseline/utils';
import { CE1BaselineInputFileContainer } from '@components/ce1-baseline/InputFileContainer';
import { CE1_BASELINE_IMPORT_CONFIG } from '@components/ce1-baseline/config';

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => {
      reader.abort();
      reject(e.target.error);
    };
    reader.readAsText(file);
  });

const parseBaselineCSV = (csvText) => {
  const results = parse(csvText, { header: true });
  const firstRow = trimObjectKeys(results.data[0]);
  return JSON.parse(firstRow.json);
};

const validationSchema = object({
  ...getValidationSchemaFromColsConfig(CE1_BASELINE_IMPORT_CONFIG),
});

const ImportingBody = () => (
  <>
    <Spacer y={20} />
    Importing baseline data... Please do not close this window or press cancel.
    <Spacer y={20} />
    <InlineLoader />
    <Spacer y={20} />
  </>
);

const FileUploadBody = ({ numberOfMonths, rateCardLabel }) => (
  <Form autoComplete="off">
    <CE1BaselineInputFileContainer disableOnFormErrors={false} />
    <Spacer y={10} />
    <TinyHeaderSpan headerText="Notes:" />
    <Spacer y={10} />
    <BulletedList
      items={[
        `Number of months in the deal: ${numberOfMonths}`,
        `Your current rate card is "${rateCardLabel}"; the uploaded file must use this same rate card.`,
        'Connector cores: Oracle XStream connectors default to 32 cores; all other connectors default to 0 cores.',
        'For connectors spanning multiple clouds or regions, the destination provider and region will be assigned arbitrarily. Please correct manually after import.',
      ]}
    />
    <Spacer y={40} />
  </Form>
);

export const CE1BaselineImportModal = ({ isOpen, setOpen }) => {
  const { estimateId } = useParams();
  const estimateVersion = useEstimateVersion();
  const numberOfMonths = useEstimateDealDurationMonths();
  const rateCardVersion = useEstimateRateCardVersion();
  const rateCards = useEstimateRateCards();
  const rateCardLabel = getRateCardLabelByName(rateCards, rateCardVersion);
  const [isImporting, setIsImporting] = useState(false);
  const estimate = useContext(EstimateContext);
  const metaData = useContext(MonthlyInputsMetaDataContext);

  const [addCluster] = useAddClusterMutation();
  const [updateCluster] = useUpdateClusterMutation();
  const [addFlinkPool] = useAddFlinkPoolMutation();
  const [updateFlinkPool] = useUpdateFlinkPoolMutation();
  const [createStreamGovernanceConfig] = useCreateStreamGovernanceConfigurationMutation();

  const initialValues = {};

  const handleImport = async (formik) => {
    if (isImporting) {
      return;
    }
    setIsImporting(true);

    try {
      const csvText = await readFileAsText(formik.values.input_file);
      const jsonData = parseBaselineCSV(csvText);

      await processBaselineImport(
        jsonData,
        estimateId,
        estimateVersion,
        numberOfMonths,
        {
          addCluster,
          updateCluster,
          addFlinkPool,
          updateFlinkPool,
          createStreamGovernanceConfig,
        },
        estimate,
        metaData,
        rateCardLabel
      );
    } catch (error) {
      toastError(null, `Baseline import failed: ${error.message || error}`);
    } finally {
      setIsImporting(false);
      formResetHandler(formik, setOpen);
    }
  };

  return (
    <StyledContainer data-testid="baseline-import-modal">
      <Formik
        autoComplete="off"
        initialValues={initialValues}
        onSubmit={() => {}}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <ConfirmModal
            body={
              <StyledContainer>
                <IfElse
                  elseContent={
                    <FileUploadBody numberOfMonths={numberOfMonths} rateCardLabel={rateCardLabel} />
                  }
                  ifContent={<ImportingBody />}
                  test={isImporting}
                />
              </StyledContainer>
            }
            cancelButtonNegative={true}
            disabled={isImporting || !formik.dirty || !formik.isValid}
            header="Import Baseline"
            isOpen={isOpen}
            okButtonNegative={false}
            okButtonText={isImporting ? 'Importing...' : 'Import'}
            onClickHandlerForCancel={() => {
              if (!isImporting) {
                formResetHandler(formik, setOpen);
              }
            }}
            onClickHandlerForOK={() => handleImport(formik)}
          />
        )}
      </Formik>
    </StyledContainer>
  );
};
